import React, { useState, useEffect, useRef, useMemo, memo, useCallback, useContext } from 'react';
import { 
  Box, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, 
  Paper, Typography, Card, CardContent, Divider, Chip, TablePagination, CircularProgress, Button,
  TextField, MenuItem, TableSortLabel,
  IconButton, Tooltip, FormControl,
  DialogTitle, DialogContent, DialogActions
} from '@mui/material';
import Grid from "./ContainerGrid";
import moment from 'moment';
import {numeral } from "../utils";
import ManagedDropdown from "./ManagedDropdown";

import { useQuickAction } from './DataQuickActionContext'; 
import AuthContext from './auth/Context';
import ManagedModal from './ManagedModal';
import CodeEditor from './CodeEditor';
import DynamicCustomReport from './DynamicCustomReport';

import SaveIcon from '@mui/icons-material/Save';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import CodeIcon from '@mui/icons-material/Code';
import {getFractionDigits} from "../utils";


const IOS_BLUE = '#007aff';
const IOS_RED = '#ff3b30';

const BUTTON_ACTIONS = new Map();

// Tiền tố mã cấu hình để KHÔNG lẫn với các cấu hình Pivot (DataPivotViewer dùng code = mã báo cáo)
const VIEW_CONFIG_PREFIX = 'view_';

const HELP_TEXT_CONTENT = `Tài liệu hướng dẫn Tùy chỉnh Giao diện Báo cáo (Custom View)

Trình soạn thảo này cho phép bạn dùng **React**, **Material-UI (MUI)** và **Google Charts** để thiết kế lại hoàn toàn cách hiển thị của báo cáo danh sách. Mã của bạn hoạt động như phần thân (body) của một React Functional Component.

Bạn **bắt buộc phải dùng từ khóa \`return\`** để trả về giao diện (JSX).

#### 🛠 Các biến (Variables) có sẵn:

**1. \`data\` (Array):** Danh sách dòng dữ liệu ĐANG hiển thị — đã áp dụng bộ lọc nhanh trên đầu cột và sắp xếp của người dùng. Đây là biến bạn nên dùng để vẽ.

**2. \`rawData\` (Array):** Toàn bộ dữ liệu gốc báo cáo trả về (chưa lọc nhanh, chưa sắp xếp).

**3. \`columns\` (Array):** Định nghĩa cột đang hiển thị, mỗi phần tử có \`field\`, \`label\`, \`type\` (String | Number | Date | DateTime | Boolean | Button), \`config\`...

**4. \`condition\` (Object):** Điều kiện lọc mà người dùng đang áp dụng cho báo cáo (ngày từ/đến, mã kho, mã khách...).

**5. \`report\` (Object):** Các tiện ích thao tác với báo cáo:
* \`report.refresh()\`: tải lại dữ liệu báo cáo.
* \`report.loadMore()\`: tải thêm trang tiếp theo (nếu \`report.hasNextPage\` = true).
* \`report.totalRecords\`, \`report.hasNextPage\`, \`report.isFetching\`: trạng thái dữ liệu.
* \`report.openReport(code, title, null, null, condition)\`: mở drill-down sang báo cáo khác.
* \`report.openEdit(apiCode, null, null, { _id })\`: mở form chứng từ/danh mục.

**6. \`context\` (Object):** App context (token, \`context.apis\`, \`context.alert\`, \`context.appInfo\`...).

**7. Các thư viện toàn cục:**
* \`MUI\`: toàn bộ components Material-UI (VD: \`<MUI.Box>\`, \`<MUI.Typography>\`). MUI phiên bản 7, Grid dùng thuộc tính \`size\`.
* \`ContainerGrid\`: Grid co giãn theo container thay vì theo màn hình thiết bị.
* \`React\`: để gọi Hooks (\`React.useState\`, \`React.useMemo\`).
* \`Chart\`: component \`<Chart />\` của \`react-google-charts\`.
* \`moment\`, \`numeral\`: định dạng ngày và số.

#### 📌 Ví dụ nhanh

\`\`\`jsx
const tong = data.reduce((s, r) => s + (Number(r.tien) || 0), 0);
return (
  <MUI.Box>
    <MUI.Typography variant="h6">Tổng tiền: {numeral(tong).format('0,0')}</MUI.Typography>
    {data.map((row, i) => (
      <MUI.Paper key={i} sx={{ p: 1.5, mb: 1 }}>
        <b>{row.ma_kh}</b> — {numeral(row.tien).format('0,0')}
      </MUI.Paper>
    ))}
  </MUI.Box>
);
\`\`\`
`;

// ============================================================================
// 1. CÁC HÀM HELPER ĐƯỢC ĐƯA RA NGOÀI ĐỂ TRÁNH TẠO LẠI KHI RENDER
// ============================================================================
const excludedFields = ['ten_trang_thai', 'color', 'picture', 'hinh_anh'];
const STT_WIDTH = 50;

const headerFuncMap = new Map();
const renderTableHeader = (col)=>{
    if(!col.custom_header) return col.label || col.header || col.field;
    try{
        let func;
        if(!headerFuncMap.has(col.custom_header)){
            let func_string =col.custom_header;
            if(func_string.indexOf("return ")<0) func_string = "return " + func_string;
            func = new Function("column","header","moment","numeral",func_string);
            headerFuncMap.set(col.custom_header,func);
        }else{
            func = headerFuncMap.get(col.custom_header);
        }
        let label = func(col,col.label||col.field,moment,numeral);
        return label!=undefined? <Typography dangerouslySetInnerHTML={{__html:label}}/>:(col.label || col.header || col.field)
    }catch(e){
        console.error("[ReportDataView][renderTableHeader]",e);
        return col.label || col.header || col.field;
    }
}

const valueFuncMap = new Map();
const renderCellValue = (row, col, condition, context, reportData = [], onRefresh = undefined, reRenderRow = undefined) => {
  const {openReport} = useQuickAction();
  if(col.custom_render){
      try{
          let func;
          if(!valueFuncMap.has(col.custom_render)){
              let func_string =col.custom_render||"";
              if(func_string.indexOf("return ")<0) func_string = "return " + func_string;
              func = new Function("row","column","condition","moment","numeral","context",func_string);
              valueFuncMap.set(col.custom_render,func);
          }else{
              func = valueFuncMap.get(col.custom_render);
          }
          let render_content = func(row,{...col},condition,moment,numeral,context);
          //console.log("[renderCellValue] render content:",{row:row,column:{...col,field:col},script:col.custom_render});
          if(render_content!=undefined) return <Typography dangerouslySetInnerHTML={{__html:render_content}}/>
      }catch(e){
          console.error("[ReportDataView][renderCellValue]",e)
      }
  }
  
  // --- XỬ LÝ TYPE LÀ BUTTON Ở ĐÂY ---
  if (col.type === 'Button') {
      return (
          <Button
              variant="outlined"
              size="small"
              onClick={(e) => {
                  e.stopPropagation(); // Ngăn việc trigger sự kiện onRowClick của dòng/card (nếu có)

                  col.handle_click = col.handle_click || "alert('Chưa khai báo xử lý cho button này')";
                  const report = {
                    refresh: ()=>{
                      if(onRefresh) onRefresh();
                    },
                    reRender:()=>{
                      // Cập nhật state để ép component con rerender lại dòng tương ứng
                      if (reRenderRow) reRenderRow(row);
                    },
                    drilldownRef:{
                      open:(type,code,condition,title,options={})=>{
                        if(type=="report"){
                          return openReport(code, title, null, null, condition,options);
                        }
                        console.error("[ReportDataViewer] chưa triển khai kiểu drilldown",{type,code});
                      }
                    }
                  }
                  
                  if(!BUTTON_ACTIONS.has(col.handle_click)){
                    let func_string = `(async ()=>{
                      ${col.handle_click}
                    })();`
                    const hanldeButtonClick = new Function("row","column","context","report","moment","numeral","condition",func_string);
                    BUTTON_ACTIONS.set(col.handle_click,hanldeButtonClick)
                  }

                  const hanldeButtonClick = BUTTON_ACTIONS.get(col.handle_click);
                  try{
                    let row_orgin = reportData.find(r=>r.__key && r.__key===row?.__key) || row;
                    hanldeButtonClick(row_orgin,col,context,report,moment,numeral,condition);
                  }catch(e){
                    console.error("[ReportDataViewer][CellButton] err:",e,col.handle_click);
                    context.alert?.(e.message || e.error || e,null,"red") || alert(e.message || e.error || e);
                  }
              }}
          >
              {row[col.field] || col.label || 'Nhấn'}
          </Button>
      );
  }
  // ----------------------------------

  const val = row[col.field];
  if (val === null || val === undefined || val === '') return '';
  if (col.type === 'Number'){
    return numeral(val).format(col.config?.format);
  } 

  if (col.type === 'Date' && val) return moment(val).format('DD/MM/YYYY');
  if (col.type === 'DateTime' && val) return moment(val).format('DD/MM/YYYY HH:mm');
  if (col.type === 'Boolean') return val ? 'Có' : 'Không';
  return val;
};

const getTextColor = (row, col, defaultColor = 'inherit') => {
  const val = row[col.field];
  if (col.type === 'Number' && val !== null && val !== undefined && val !== '') {
    if (Number(val) < 0) return IOS_RED; 
  }
  return defaultColor;
};


// ============================================================================
// 2. COMPONENT MEMO CHO TỪNG DÒNG CỦA BẢNG (TABLE ROW)
// ============================================================================
const MemoizedReportTableRow = memo(({
  row, rowIndex, columns, page, currentLimit, titleField, 
  renderTableRow, onRowClick, condition, context, reportData = [], onRefresh = undefined,
  version, reRenderRow // Nhận thêm version và reRenderRow từ parent
}) => {
  const isBoldRow = row.bold === true || row.isBold === true;
  const rowBgColor = isBoldRow ? '#fff9c4' : 'white';
  
  if (renderTableRow) return renderTableRow(row, rowIndex, onRowClick);

  return (
    <TableRow 
      hover 
      onClick={onRowClick ? () => onRowClick(row) : undefined} 
      sx={{ 
        bgcolor: isBoldRow ? '#fff9c4' : 'inherit', 
        cursor: onRowClick ? 'pointer' : 'default', 
        '&:hover': onRowClick ? { bgcolor: 'rgba(0, 122, 255, 0.08)' } : {} 
      }}
    >
      <TableCell align="center" sx={{ position: 'sticky', left: 0, zIndex: 2, bgcolor: rowBgColor, border: '1px solid #e0e0e0', fontWeight: isBoldRow ? 'bold' : 'normal' }}>
        {page * currentLimit + rowIndex + 1}
      </TableCell>
      {columns.map((col, colIndex) => {
        const isSticky = col.field === titleField;
        return (
          <TableCell 
            key={colIndex} 
            align={col.type === 'Number' ? 'right' : 'left'} 
            sx={{ 
              color: getTextColor(row, col, 'text.primary'), 
              fontWeight: isBoldRow ? 'bold' : 'normal', 
              border: '1px solid #e0e0e0', 
              ...(isSticky && { position: 'sticky', left: STT_WIDTH, zIndex: 2, bgcolor: rowBgColor }) 
            }}
          >
            {renderCellValue(row, col, condition, context, reportData, onRefresh, reRenderRow)}
          </TableCell>
        );
      })}
    </TableRow>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.row === nextProps.row &&
    prevProps.page === nextProps.page &&
    prevProps.currentLimit === nextProps.currentLimit &&
    prevProps.columns === nextProps.columns &&
    prevProps.version === nextProps.version // Kiểm tra version để phá vỡ memo khi cần
  );
});


// ============================================================================
// 3. COMPONENT MEMO CHO TỪNG THẺ CARD (CARD ITEM)
// ============================================================================
const MemoizedReportCardItem = memo(({ 
  row, rowIndex, columns, titleField, subTitleCol, 
  renderCardItem, onRowClick, condition, context, reportData, onRefresh,
  version, reRenderRow // Nhận thêm version và reRenderRow từ parent
}) => {
  if (renderCardItem) return renderCardItem(row, rowIndex, onRowClick);

  const isBoldRow = row.bold === true || row.isBold === true;
  let visibleCols = columns.filter(c => {
    if (c.field === titleField || c.field === subTitleCol?.field || excludedFields.includes(c.field)) return false;
    
    // Nút Button vẫn nên hiển thị kể cả khi dữ liệu dòng đó trống
    if (c.type === 'Button') return true;

    const val = row[c.field];
    return val !== undefined && val !== null && val !== '' && !(Array.isArray(val) && val.length === 0);
  });

  const statusColor = row.color || null;
  const statusLabel = row.ten_trang_thai || null;

  return (
    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
      <Card 
        onClick={onRowClick ? () => onRowClick(row) : undefined} 
        sx={{ height: '100%', borderRadius: '12px', boxShadow: '0 1px 8px rgba(0,0,0,0.04)', border: '1px solid #e5e5ea', display: 'flex', flexDirection: 'column', overflow: 'hidden', bgcolor: isBoldRow ? '#fff9c4' : 'white', cursor: onRowClick ? 'pointer' : 'default', transition: 'all 0.2s ease-in-out', '&:active': onRowClick ? { transform: 'scale(0.98)' } : {}, '&:hover': onRowClick ? { boxShadow: '0 4px 12px rgba(0,0,0,0.12)', borderColor: IOS_BLUE } : { boxShadow: '0 4px 12px rgba(0,0,0,0.08)' } }}
      >
        <Box sx={{ display: 'flex', flexGrow: 1 }}>
          {statusColor && <Box sx={{ width: 4, bgcolor: statusColor, flexShrink: 0 }} />}
          <CardContent sx={{ p: '14px !important', flexGrow: 1, width: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ display: 'flex', gap: 1.5, mb: visibleCols.length > 0 ? 1 : 0 }}>
              <Box sx={{ flexGrow: 1, overflow: 'hidden' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.2, flexGrow: 1, pr: 1, overflow: 'hidden' }}>
                    <Typography variant="subtitle1" fontWeight="bold" color={IOS_BLUE} sx={{ lineHeight: 1.2, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                      {columns.find(c => c.field === titleField) ? renderCellValue(row, columns.find(c => c.field === titleField), condition, context, reportData, onRefresh, reRenderRow) : `Bản ghi #${rowIndex + 1}`}
                    </Typography>
                    {subTitleCol && row[subTitleCol.field] && (
                      <Typography variant="body2" color="text.secondary" noWrap sx={{ fontWeight: isBoldRow ? 'bold' : 500 }}>{renderCellValue(row, subTitleCol, condition, context, reportData, onRefresh, reRenderRow)}</Typography>
                    )}
                    {statusLabel && <Chip label={statusLabel} size="small" sx={{ mt: 0.5, alignSelf: 'flex-start', fontWeight: 'bold', fontSize: '0.65rem', height: 20, bgcolor: statusColor || '#8e8e93', color: 'white' }} />}
                  </Box>
                </Box>
              </Box>
            </Box>
            {visibleCols.length > 0 && (
              <Box sx={{ mt: 'auto', pt: 1 }}> 
                <Divider sx={{ mb: 1.5, borderStyle: 'dashed', borderColor: '#e5e5ea' }} />
                <Grid container spacing={1.5}>
                  {visibleCols.map(col => (
                    <Grid size={{ xs: 6 }} key={col.field}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2, mb: 0.2 }}>{col.label || col.field}</Typography>
                        <Typography component="div" variant="body2" color={getTextColor(row, col, '#1c1c1e')} fontWeight={isBoldRow ? 'bold' : '500'} sx={{ wordBreak: 'break-word', lineHeight: 1.3, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                          {renderCellValue(row, col, condition, context, reportData, onRefresh, reRenderRow)}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}
          </CardContent>
        </Box>
      </Card>
    </Grid>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.row === nextProps.row && 
    prevProps.columns === nextProps.columns &&
    prevProps.version === nextProps.version // Kiểm tra version để phá vỡ memo
  );
});


// ============================================================================
// 4. COMPONENT GỐC BỌC MEMO CHỐNG RERENDER TỪ CHA
// ============================================================================
const ReportDataViewer = memo(({ 
  data = [],
  condition,
  context,
  columns = [], 
  viewMode="table", 
  renderTableRow, 
  renderCardItem,
  hasNextPage = false,
  onLoadMore,
  isFetching = false,
  onRowClick,
  scrollRef,
  onScroll,
  currentLimit=50,
  onLimitChange,
  totalRecords=0,
  onRefresh,
  onViewModeChange,
  // ===== CUSTOM VIEW =====
  reportCode,          // mã báo cáo — dùng làm khoá lưu/tải góc nhìn tuỳ chỉnh
  apiCode,             // mã api báo cáo (chỉ để lưu kèm cấu hình cho dễ truy vết)
  filterFields,        // khai báo bộ lọc (lưu kèm cấu hình)
  enableCustomView = true,
  openScriptEditorSignal // bộ đếm: tăng lên 1 lần = mở trình soạn thảo (bấm nút custom trên header)
}) => {
  
  const authContext = useContext(AuthContext);
  const appContext = context || authContext;
  const quickAction = useQuickAction();

  const [inlineFilters, setInlineFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const dialogPageSelectRef = useRef(null);

  // State quản lý phiên bản cập nhật của từng row
  const [rowVersions, setRowVersions] = useState({});

  const handleReRenderRow = useCallback((updatedRow) => {
    setRowVersions(prev => {
        const key = updatedRow.__key || updatedRow._id;
        if (key) {
            return { ...prev, [key]: (prev[key] || 0) + 1 };
        }
        // Fallback: ép cập nhật toàn bộ nếu không xác định được ID (hiếm gặp)
        return { ...prev, forceAll: (prev.forceAll || 0) + 1 };
    });
  }, []);

  const handleInlineFilterChange = (field, value) => {
    setInlineFilters(prev => ({ ...prev, [field]: value }));
    setPage(0); 
  };

  const handleSortClick = (field) => {
    setSortConfig(prev => {
      if (prev.key === field) {
        if (prev.direction === 'asc') return { key: field, direction: 'desc' };
        return { key: null, direction: 'asc' }; 
      }
      return { key: field, direction: 'asc' }; 
    });
    setPage(0); 
  };

  const processedData = useMemo(() => {
    let filtered = data;
    if (!Object.keys(inlineFilters).every(k => inlineFilters[k] === '' || inlineFilters[k] === 'all')) {
      filtered = data.filter(row => {
        return columns.every(col => {
          const filterVal = inlineFilters[col.field];
          if (filterVal === undefined || filterVal === '' || filterVal === 'all') return true; 

          const cellVal = row[col.field];
          if (cellVal === null || cellVal === undefined) return false;

          if (col.type === 'Boolean') return !!cellVal === (filterVal === 'true');
          return String(cellVal).toLowerCase().includes(String(filterVal).toLowerCase());
        });
      });
    }

    if (sortConfig.key) {
      const colDef = columns.find(c => c.field === sortConfig.key);
      const isNumeric = colDef && colDef.type === 'Number';

      filtered = [...filtered].sort((a, b) => {
        let valA = a[sortConfig.key];
        let valB = b[sortConfig.key];

        if (valA === null || valA === undefined) valA = isNumeric ? -Infinity : '';
        if (valB === null || valB === undefined) valB = isNumeric ? -Infinity : '';

        if (isNumeric) {
          valA = Number(valA);
          valB = Number(valB);
        } else {
          valA = String(valA).toLowerCase();
          valB = String(valB).toLowerCase();
        }

        if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
        if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
        return 0;
      });
    }
    return filtered;
  }, [data, columns, inlineFilters, sortConfig]);


  const [page, setPage] = useState(0);
  const prevDataLengthRef = useRef(data.length);
  useEffect(() => {
    if (data.length < prevDataLengthRef.current) setPage(0);
    prevDataLengthRef.current = data.length;
  }, [data.length]);

  const handleChangePage = (event, newPage) => {
    // Chặn việc đổi trang nếu hệ thống đang tải dữ liệu
    if (isFetching) return;

    const requiredLength = (newPage + 1) * currentLimit;
    if (requiredLength > processedData.length && hasNextPage && onLoadMore) {
      onLoadMore();
    }
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    //  Chặn việc đổi số dòng nếu hệ thống đang tải
    if (isFetching) return;

    if (onLimitChange) onLimitChange(parseInt(event.target.value, 10));
    setPage(0);
  };

  const titleColDef = columns.find(c => c.isTitle === true) || columns.find(c => c.type === 'String' && !excludedFields.includes(c.field)) || columns[0];
  const titleField = titleColDef?.field;
  let rowsPerPageOptions = [30, 50, 100, 200,5000000];
  if(!rowsPerPageOptions.find(r=>r===currentLimit)){
    rowsPerPageOptions.push(currentLimit);
    rowsPerPageOptions = rowsPerPageOptions.sort((a,b)=>a-b);
  }

  // ==========================================================================
  // 5. CUSTOM VIEW — GÓC NHÌN TUỲ CHỈNH BẰNG SCRIPT (giống DataPivotViewer)
  //  - Lưu/tải nhiều "góc nhìn" vào model pivotconfig với code = view_<mã báo cáo>
  //    (tiền tố để KHÔNG lẫn với cấu hình Pivot vốn dùng code = <mã báo cáo>).
  //  - Ghi nhớ góc nhìn dùng lần cuối trong localStorage của từng người dùng.
  // ==========================================================================
  const [savedConfigs, setSavedConfigs] = useState([]);
  const [selectedConfigId, setSelectedConfigId] = useState('');
  const [script, setScript] = useState('');
  const [showScriptEditor, setShowScriptEditor] = useState(false);
  const [displayMode, setDisplayMode] = useState(viewMode === 'custom' ? 'custom' : (viewMode === 'card' ? 'card' : 'table'));

  // Thanh công cụ CHỈ xuất hiện khi người dùng đã vào chế độ "Giao diện tuỳ chỉnh",
  // và chỉ chứa những thứ header không có: chọn góc nhìn, soạn mã, lưu.
  const canCustomView = !!enableCustomView && !!reportCode;
  const configCode = reportCode ? `${VIEW_CONFIG_PREFIX}${reportCode}` : '';
  const userToken = appContext?.userInfo?.token || (typeof appContext?.getToken === 'function' ? appContext.getToken() : '');
  const viewStorageKey = `flexbiz_last_report_view_${appContext?.userInfo?.email || 'public'}_${reportCode}`;

  // Chế độ xem do NHÓM NÚT TRÊN HEADER quyết định (bảng / thẻ / pivot / custom) —
  // viewer không dựng lại nhóm nút đó nữa để tránh trùng lặp giao diện.
  useEffect(() => {
    if (viewMode === 'table' || viewMode === 'card' || viewMode === 'custom') setDisplayMode(viewMode);
  }, [viewMode]);

  // Nếu báo cáo không bật custom view mà localStorage còn nhớ chế độ 'custom' -> quay về bảng.
  const effectiveMode = (displayMode === 'custom' && !(enableCustomView && reportCode)) ? 'table' : displayMode;

  const applyViewConfig = useCallback((configId, configList) => {
    const list = configList || savedConfigs;
    setSelectedConfigId(configId || '');
    if (!configId) { setScript(''); return; }
    const configObj = list.find(c => c._id === configId || c.id === configId);
    if (configObj && configObj.config) {
      try {
        const parsed = JSON.parse(configObj.config);
        setScript(parsed.script || '');
        // CỐ Ý không đổi chế độ xem ở đây: chế độ xem chỉ do nhóm nút trên header quyết định,
        // nếu không header sẽ sáng "Bảng" trong khi nội dung lại là giao diện tuỳ chỉnh.
      } catch (err) { console.error('[ReportDataViewer] Cấu hình góc nhìn không hợp lệ:', err); }
    }
  }, [savedConfigs]);

  const fetchViewConfigs = useCallback(async () => {
    if (!configCode || !appContext?.apis?.asyncGetList) return;
    try {
      const res = await appContext.apis.asyncGetList(userToken, 'pivotconfig', { condition: { code: configCode }, limit: 100, sort: { _id: -1 } });
      const list = res?.data || res || [];
      setSavedConfigs(list);
      // CHỈ tự áp dụng góc nhìn mà chính người dùng đã chọn lần trước — tránh việc
      // một góc nhìn của người khác tự nhảy ra thay cho bảng dữ liệu mặc định.
      let lastUsedId = null;
      try { lastUsedId = localStorage.getItem(viewStorageKey); } catch (e) {}
      if (lastUsedId && list.some(c => (c._id || c.id) === lastUsedId)) applyViewConfig(lastUsedId, list);
    } catch (e) { console.error('[ReportDataViewer] Lỗi tải góc nhìn tuỳ chỉnh:', e); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [configCode, userToken]);

  useEffect(() => { if (canCustomView) fetchViewConfigs(); }, [canCustomView, fetchViewConfigs]);

  // Header bấm lại nút "Giao diện tuỳ chỉnh" -> mở trình soạn thảo
  useEffect(() => {
    if (openScriptEditorSignal && canCustomView) setShowScriptEditor(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openScriptEditorSignal]);

  const handleSelectViewConfig = (e) => {
    const configId = e.target.value;
    applyViewConfig(configId);
    try {
      if (configId) localStorage.setItem(viewStorageKey, configId);
      else localStorage.removeItem(viewStorageKey);
    } catch (err) {}
  };

  // Vào chế độ custom từ trong viewer (VD sau khi bấm "Áp dụng và xem thử"):
  // báo ngược lên header để nhóm nút sáng đúng và ghi nhớ lựa chọn.
  const switchToCustom = () => {
    setDisplayMode('custom');
    if (onViewModeChange) onViewModeChange('custom');
  };

  const handleSaveViewConfig = () => {
    if (!configCode) return;
    if (!quickAction?.openAdd) {
      console.warn('[ReportDataViewer] Thiếu QuickActionProvider nên không lưu được góc nhìn.');
      return;
    }
    if (!script) {
      appContext?.alert?.('Chưa có mã giao diện để lưu. Hãy bấm nút </> để soạn thảo trước.', null, 'red');
      return;
    }
    const currentSettings = { viewType: 'report', reportCode, apiCode, displayMode, script, columns, filterFields, condition };
    const configString = JSON.stringify(currentSettings, null, 2);
    const formFields = [
      { field: 'title', label: 'Tên góc nhìn', type: 'String', required: true, gridEdit: { xs: 12 } },
      { field: 'code', label: 'Mã', type: 'String', hideInForm: true },
      { field: 'config', label: 'Cấu hình', type: 'String', hideInForm: true }
    ];
    const onSuccess = (savedItem) => {
      const newId = savedItem._id || savedItem.id;
      try { localStorage.setItem(viewStorageKey, newId); } catch (e) {}
      setSelectedConfigId(newId);
      fetchViewConfigs();
      appContext?.alert?.('Đã lưu góc nhìn thành công!', null, 'success');
    };
    if (selectedConfigId) {
      const existingConf = savedConfigs.find(c => c._id === selectedConfigId || c.id === selectedConfigId);
      quickAction.openEdit('pivotconfig', formFields, 'Cập nhật góc nhìn', { ...existingConf, config: configString }, onSuccess);
    } else {
      quickAction.openAdd('pivotconfig', formFields, 'Lưu góc nhìn mới', { code: configCode, config: configString }, onSuccess);
    }
  };

  const handleSaveViewAsNew = () => { setSelectedConfigId(''); setTimeout(() => handleSaveViewConfig(), 100); };

  // Bộ tiện ích bơm vào script của người dùng
  const reportApi = useMemo(() => ({
    refresh: () => { if (onRefresh) onRefresh(); },
    loadMore: () => { if (onLoadMore && hasNextPage && !isFetching) onLoadMore(); },
    hasNextPage,
    isFetching,
    totalRecords,
    currentLimit,
    onRowClick,
    openReport: quickAction?.openReport,
    openEdit: quickAction?.openEdit,
  }), [onRefresh, onLoadMore, hasNextPage, isFetching, totalRecords, currentLimit, onRowClick, quickAction]);

  const renderViewToolbar = () => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', flexShrink: 0, px: 1.5, py: 0.75, mb: 1, borderBottom: '1px solid #e0e0e0' }}>
      <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
        Giao diện tuỳ chỉnh
      </Typography>

      <FormControl size="small" sx={{ minWidth: 190, bgcolor: 'white' }}>
        <ManagedDropdown type="select" displayEmpty value={selectedConfigId} onChange={handleSelectViewConfig} sx={{ height: 28, fontSize: '0.8rem' }}>
          <MenuItem value="" sx={{ fontStyle: 'italic' }}>--- Chưa đặt tên ---</MenuItem>
          {savedConfigs.map(c => (<MenuItem key={c._id || c.id} value={c._id || c.id}>{c.title}</MenuItem>))}
        </ManagedDropdown>
      </FormControl>

      <Box sx={{ flexGrow: 1 }} />

      <Tooltip title="Soạn thảo mã nguồn hiển thị">
        <IconButton size="small" onClick={() => setShowScriptEditor(true)} sx={{ border: '1px solid #e0e0e0', bgcolor: 'white' }}>
          <CodeIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <Tooltip title={selectedConfigId ? 'Cập nhật góc nhìn đang chọn' : 'Lưu thành góc nhìn có tên'}>
        <IconButton size="small" onClick={handleSaveViewConfig} sx={{ border: '1px solid #e0e0e0', bgcolor: 'white' }}>
          <SaveIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      {selectedConfigId && (
        <Tooltip title="Lưu thành góc nhìn mới">
          <IconButton size="small" onClick={handleSaveViewAsNew} sx={{ border: '1px solid #e0e0e0', bgcolor: 'white' }}>
            <AddCircleOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );

  const renderCustomView = () => {
    if (!script) {
      return (
        <Box sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column', gap: 1.5, justifyContent: 'center', alignItems: 'center', color: 'text.secondary' }}>
          <Typography>Chưa có mã giao diện tuỳ chỉnh cho báo cáo này.</Typography>
          <Button variant="outlined" startIcon={<CodeIcon />} onClick={() => setShowScriptEditor(true)} sx={{ textTransform: 'none', borderRadius: 2 }}>
            Soạn thảo giao diện
          </Button>
        </Box>
      );
    }
    return (
      <DynamicCustomReport
        script={script}
        data={processedData}
        rawData={data}
        columns={columns}
        condition={condition}
        context={appContext}
        report={reportApi}
      />
    );
  };

  const renderTable = () => {
    const paginatedData = processedData.slice(page * currentLimit, page * currentLimit + currentLimit);
    let count = totalRecords;
    if (Object.keys(inlineFilters).find(k => inlineFilters[k] !== '' && inlineFilters[k] !== 'all')) {
        count = hasNextPage ? -1 : processedData.length;
    }

    return (
      <Paper elevation={0} sx={{ border: '1px solid #e0e0e0', borderRadius: 2, display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
        <TableContainer ref={scrollRef} onScroll={onScroll} sx={{ flexGrow: 1, overflow: 'auto' }}>
          <Table stickyHeader size="small" sx={{ borderCollapse: 'collapse', minWidth: 800 }}>
            <TableHead>
              <TableRow>
                <TableCell align="center" sx={{ position: 'sticky', left: 0, top: 0, zIndex: 5, minWidth: STT_WIDTH, width: STT_WIDTH, bgcolor: '#e3f2fd', border: '1px solid #cfd8dc', verticalAlign: 'top', pt: 1.5 }}>
                  <Typography fontWeight="bold" fontSize="0.85rem">STT</Typography>
                </TableCell>

                {columns.map((col, index) => {
                  const isSticky = col.field === titleField;
                  const filterVal = inlineFilters[col.field] ?? (col.type === 'Boolean' ? 'all' : '');
                  const isSorted = sortConfig.key === col.field;

                  return (
                    <TableCell 
                      key={index} align={col.type === 'Number' ? 'right' : 'left'} 
                      sx={{ bgcolor: isSticky ? '#e3f2fd' : '#f8f9fa', border: '1px solid #cfd8dc', verticalAlign: 'top', minWidth: 140, position: 'sticky', top: 0, zIndex: isSticky ? 5 : 4, ...(isSticky && { left: STT_WIDTH }) }}
                    >
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', overflow: 'hidden' }}>
                          <TableSortLabel
                            active={isSorted} direction={isSorted ? sortConfig.direction : 'asc'} onClick={() => handleSortClick(col.field)}
                            sx={{ fontWeight: 'bold', fontSize: '0.85rem', display: 'inline-flex', flexDirection: 'row', flexWrap: 'nowrap', whiteSpace: 'nowrap', '& .MuiTableSortLabel-icon': { margin: 0, marginLeft: '4px' }, '&:hover': { color: 'primary.main' } }}
                          >
                            {renderTableHeader(col)}
                          </TableSortLabel>
                        </Box>
                        {col.type === 'Boolean' ? (
                            <ManagedDropdown type="select" value={filterVal} onChange={(e) => handleInlineFilterChange(col.field, e.target.value)} size="small" sx={{ height: 28, fontSize: '0.8rem', bgcolor: 'white', '.MuiSelect-select': { py: 0.5 } }}>
                                <MenuItem value="all">Tất cả</MenuItem>
                                <MenuItem value="true">Có</MenuItem>
                                <MenuItem value="false">Không</MenuItem>
                            </ManagedDropdown>
                        ) : (
                            <TextField value={filterVal} onChange={(e) => handleInlineFilterChange(col.field, e.target.value)} placeholder="Lọc..." size="small" autoComplete="off" sx={{ '& .MuiInputBase-root': { height: 28, fontSize: '0.8rem', bgcolor: 'white' }, '& .MuiOutlinedInput-input': { py: 0.5, px: 1 } }} />
                        )}
                      </Box>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>

            <TableBody>
              {paginatedData.length === 0 && isFetching ? (
                <TableRow><TableCell colSpan={columns.length + 1} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
              ) : paginatedData.length === 0 ? (
                <TableRow><TableCell colSpan={columns.length + 1} align="center" sx={{ py: 4, color: 'text.secondary' }}>Không có dữ liệu</TableCell></TableRow>
              ) : (
                paginatedData.map((row, rowIndex) => {
                  const rowKey = row.__key || row._id || rowIndex;
                  const currentVersion = (rowVersions[rowKey] || 0) + (rowVersions.forceAll || 0);

                  return (
                    <MemoizedReportTableRow 
                      key={rowKey} 
                      row={row}
                      condition={condition}
                      context={context}
                      rowIndex={rowIndex} 
                      columns={columns} 
                      page={page} 
                      currentLimit={currentLimit} 
                      titleField={titleField} 
                      renderTableRow={renderTableRow} 
                      onRowClick={onRowClick}
                      reportData={data}
                      onRefresh={onRefresh}
                      version={currentVersion}
                      reRenderRow={handleReRenderRow}
                    />
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <ManagedDropdown type="tablepagination"
          ref = {dialogPageSelectRef}
          sx={{ borderTop: '1px solid #e0e0e0', flexShrink: 0, bgcolor: 'background.paper' }}
          rowsPerPageOptions={rowsPerPageOptions} component="div" count={count} rowsPerPage={currentLimit} page={page} 
          onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} labelRowsPerPage="Số dòng/trang:" 
          labelDisplayedRows={({ from, to, count }) => count !== -1 ? `${from}–${to} trên ${count}` : `${from}–${to} trên nhiều hơn`} 
        />
      </Paper>
    );
  };

  const renderCard = () => {
    const subTitleCol = columns.find(c => c.isSubTitle === true);

    return (
      <Box ref={scrollRef} onScroll={onScroll} sx={{ p: 2, flexGrow: 1, overflowY: 'auto' }}>
        {processedData.length === 0 ? (
          <Typography align="center" color="text.secondary" sx={{ py: 4 }}>Không có dữ liệu</Typography>
        ) : (
          <Grid container spacing={2}>
            {processedData.map((row, rowIndex) => {
               const rowKey = row.__key || row._id || rowIndex;
               const currentVersion = (rowVersions[rowKey] || 0) + (rowVersions.forceAll || 0);

               return (
                 <MemoizedReportCardItem 
                    key={rowKey} 
                    row={row} 
                    rowIndex={rowIndex} 
                    columns={columns} 
                    titleField={titleField} 
                    subTitleCol={subTitleCol} 
                    renderCardItem={renderCardItem} 
                    onRowClick={onRowClick}
                    condition={condition}
                    context={context}
                    reportData={data}
                    onRefresh={onRefresh}
                    version={currentVersion}
                    reRenderRow={handleReRenderRow}
                 />
               );
            })}
          </Grid>
        )}
        {hasNextPage && onLoadMore && (
           <Button fullWidth variant="text" sx={{ mt: 3, mb: 1, color: 'text.secondary', fontWeight: 'bold', borderRadius: 2, bgcolor: 'action.hover' }} onClick={() => !isFetching && onLoadMore()} disabled={isFetching}>
             {isFetching ? <CircularProgress size={20} /> : 'Tải thêm dữ liệu'}
           </Button>
        )}
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden' }}>
      {canCustomView && effectiveMode === 'custom' && renderViewToolbar()}

      {effectiveMode === 'custom' ? renderCustomView() : effectiveMode === 'card' ? renderCard() : renderTable()}

      {/* ================= DIALOG TRÌNH SOẠN THẢO CODE ================= */}
      {canCustomView && (
        <ManagedModal
          type="dialog"
          open={showScriptEditor}
          onClose={() => setShowScriptEditor(false)}
          maxWidth="lg"
          fullWidth
          PaperProps={{ sx: { height: '85vh', borderRadius: 3 } }}
        >
          <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', bgcolor: '#f8f9fa' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CodeIcon color="primary" />
              <Typography variant="h6" fontWeight="bold">Customize View Script</Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">Sử dụng React, MUI để thiết kế lại kết quả báo cáo</Typography>
          </DialogTitle>

          <DialogContent dividers sx={{ p: 0, bgcolor: '#ffffff' }}>
            <CodeEditor
              data={script}
              onChange={(e, newVal) => setScript(newVal)}
              language="javascript"
              helperText={HELP_TEXT_CONTENT}
            />
          </DialogContent>

          <DialogActions sx={{ p: 2, bgcolor: '#f8f9fa' }}>
            <Button onClick={() => setShowScriptEditor(false)} color="inherit" sx={{ textTransform: 'none' }}>Đóng</Button>
            <Button
              variant="contained"
              onClick={() => { switchToCustom(); setShowScriptEditor(false); }}
              sx={{ textTransform: 'none', borderRadius: 2 }}
            >
              Áp dụng và xem thử
            </Button>
          </DialogActions>
        </ManagedModal>
      )}
    </Box>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.data === nextProps.data &&
    prevProps.columns === nextProps.columns &&
    prevProps.viewMode === nextProps.viewMode &&
    prevProps.isFetching === nextProps.isFetching &&
    prevProps.hasNextPage === nextProps.hasNextPage &&
    prevProps.currentLimit === nextProps.currentLimit &&
    prevProps.totalRecords === nextProps.totalRecords &&
    prevProps.reportCode === nextProps.reportCode &&
    prevProps.condition === nextProps.condition &&
    prevProps.openScriptEditorSignal === nextProps.openScriptEditorSignal
  );
});

export default ReportDataViewer;